import PortalIdParser from 'PortalIdParser';
import Raven from 'raven-js';

// raven-js (the legacy Sentry SDK) does not normalize nested/large objects the
// way the modern SDK does (normalizeDepth), so we bound the walk ourselves to
// keep events under Sentry's size cap. Values chosen empirically for typical
// Apollo/network error shapes, not a hard field limit.
const MAX_DEPTH = 5;
const MAX_KEYS = 100;
const serializeValue = ({
  depth,
  seen,
  value
}) => {
  if (value === null) {
    return null;
  }
  if (typeof value === 'string' || typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : String(value);
  }
  if (typeof value === 'undefined') {
    return '[undefined]';
  }
  if (typeof value === 'bigint' || typeof value === 'symbol' || typeof value === 'function') {
    return String(value);
  }
  const objectValue = value;
  if (seen.has(objectValue)) {
    return '[Circular]';
  }
  if (depth >= MAX_DEPTH) {
    return '[MaxDepth]';
  }
  seen.add(objectValue);
  try {
    if (Array.isArray(value)) {
      const items = value.slice(0, MAX_KEYS).map(item => serializeValue({
        value: item,
        seen,
        depth: depth + 1
      }));
      if (value.length > MAX_KEYS) {
        items.push(`[Truncated: ${value.length - MAX_KEYS} more]`);
      }
      return items;
    }
    const keys = Object.keys(objectValue);
    const result = keys.slice(0, MAX_KEYS).reduce((acc, key) => {
      try {
        acc[key] = serializeValue({
          value: objectValue[key],
          seen,
          depth: depth + 1
        });
      } catch (_unused) {
        acc[key] = '[Unserializable]';
      }
      return acc;
    }, {});
    if (keys.length > MAX_KEYS) {
      result['[Truncated]'] = `${keys.length - MAX_KEYS} more`;
    }
    return result;
  } finally {
    seen.delete(objectValue);
  }
};
export const serializeError = error => {
  if (error instanceof Error) {
    return {
      errorMessage: error.message,
      errorName: error.name,
      errorStack: error.stack,
      errorDetails: serializeValue({
        value: error,
        seen: new WeakSet(),
        depth: 0
      })
    };
  }
  return {
    errorDetails: serializeValue({
      value: error,
      seen: new WeakSet(),
      depth: 0
    })
  };
};
export const reportErrorToLogFetch = ({
  action,
  additionalData,
  error,
  loggingTitle
}) => {
  const portalId = PortalIdParser.get();
  const extra = serializeValue({
    value: Object.assign({
      action
    }, serializeError(error), additionalData),
    seen: new WeakSet(),
    depth: 0
  });
  Raven.captureMessage(loggingTitle, {
    extra,
    tags: Object.assign({}, portalId && {
      hubId: `${portalId}`
    })
  });
};